import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { useLocation } from "react-router-dom";

import { GetWSMgr, ReRenderCanvas } from "../Utils/StateLib";
import { enumUISubModes } from "@coreapp/constants";
import { LocationSource } from "@coreapp/CoreApp/LocationSource";
import { useRouteState } from "@/hooks/useRouteState";
import { usePermissionCheck } from "@/hooks/usePermissionCheck";
import { useBlockerContext } from "./BlockerProvider";
import { useChartDocumentContext } from "./ChartDocumentProvider";
import { SubscribeExternalModuleReactAPI } from "@coreapp/main";

const defaultContextValue = {
  sideBarMode: null,
  sideBarExpanded: false,
  settingsOpenCtrlRef: undefined,
  settingsFloatingCtrlRef: undefined,
  customHeaderProps: {},
  sidebarBackTarget: null,
  setModeDirty: _state => {},
  setCustomHeaderProps: _state => {},
  setSidebarBackTarget: _state => {},
  handleSideBarModeChange: _mode => {},
  highlightRecord: _dataObj => {},
  navigateWithBackTarget: (_targetMode, _backTarget, _ignoreBlocker) => {},
  closeSidebar: () => {}
};

const RightSidebarContext = createContext(defaultContextValue);

const RightSidebarProvider = ({ children }) => {
  const { chartDocument } = useChartDocumentContext();
  const { shouldBlock } = useBlockerContext();
  const { canModifyBackgrounds } = usePermissionCheck();
  const { tool: toolParam, setParam: setRouteParam } = useRouteState();
  const settingsOpenCtrlRef = useRef();
  const settingsFloatingCtrlRef = useRef();
  const [isModeDirty, setModeDirty] = useState(false);
  const [sideBarMode, setSideBarMode] = useState(() => {
    const params = new URLSearchParams(LocationSource.getSearch());
    const urlTool = params.get("tool");
    if (urlTool) return urlTool;
    const cachedMode = localStorage.getItem("sidebar-mode");
    return cachedMode || null;
  });
  const [customHeaderProps, setCustomHeaderProps] = useState({});
  // Back-target for in-sidebar nav (e.g. BoxStyles → view → chevron back).
  // navigateWithBackTarget seeds this; the customHeaderProps effect renders
  // the chevron when sideBarMode matches the target's activeInMode. Sidebar
  // tool switching itself remains URL-driven via handleSideBarModeChange;
  // this state is purely the in-header UI affordance.
  const [sidebarBackTarget, setSidebarBackTarget] = useState(null);

  const [stagingState, setStagingState] = useState({ root: null, highlightedRecordIds: [] });

  const [sideBarExpanded, setSideBarExpanded] = useState(() => {
    const params = new URLSearchParams(LocationSource.getSearch());
    if (params.get("tool")) return true;
    const cachedIsExpanded = localStorage.getItem("sidebar-expanded") === "true";
    return cachedIsExpanded;
  });

  const ResizeCanvasOnChange = useCallback(
    () =>
      setTimeout(() => {
        var app = window["MyApp"];
        app.HandleResize();
        ReRenderCanvas();
      }, 500),
    []
  );

  const openSidebar = useCallback(() => {
    setSideBarExpanded(true);
    localStorage.setItem("sidebar-expanded", "true");
    ResizeCanvasOnChange();
  }, [ResizeCanvasOnChange]);

  const location = useLocation();

  // Reset header props on mode/location/backTarget changes, and re-render the
  // sidebarBackTarget chevron when the target mode is active.
  useEffect(() => {
    setCustomHeaderProps({});

    if (!sidebarBackTarget) return;
    if (sideBarMode !== sidebarBackTarget.activeInMode) return;

    setCustomHeaderProps(() => ({
      icon: "chevron-left",
      iconProps: {
        size: "xsmall",
        onClick: () => {
          setSidebarBackTarget(null);
          handleSideBarModeChange(sidebarBackTarget.mode);
        }
      },
      title: sidebarBackTarget.title
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sideBarMode, location, sidebarBackTarget]);

  // Clear the custom header when the active chart changes (new chart opened or
  // chart closed).
  const prevChartIdRef = useRef(null);
  useEffect(() => {
    const chartId = chartDocument?.state?.GetChartID?.() ?? null;
    if (chartId !== prevChartIdRef.current) {
      prevChartIdRef.current = chartId;
      setCustomHeaderProps({});
    }
  }, [chartDocument]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const wsMgr = GetWSMgr();

    // Tear down the background-editor submode.
    if (toolParam !== "background" && wsMgr?.GetSubMode() === enumUISubModes.backgroundeditor) {
      wsMgr.DoExitBackgroundEditorMode();
    }

    if (toolParam == null) return;

    if (wsMgr && toolParam === "background") {
      if (!wsMgr.GetDoc()) return;
      // Rule-driven (view-applied) backgrounds are immutable — the rule
      // engine reapplies them on next view/subchart change. Let the
      // sidebar tab switch below so the user can VIEW the background,
      // but don't enter canvas edit submode (edit UI is hidden by
      // BackgroundDisplay via the same IsViewAppliedBackground check).
      const isRuleDriven = wsMgr.fBackgroundMgr?.IsViewAppliedBackground?.();
      if (
        !isRuleDriven &&
        canModifyBackgrounds &&
        wsMgr.GetSubMode() !== enumUISubModes.backgroundeditor
      ) {
        wsMgr.DoBackgroundEditorMode();
      }
    }

    if (toolParam === sideBarMode) return;
    setSideBarMode(toolParam);
    setModeDirty(false);
    setCustomHeaderProps({});
    localStorage.setItem("sidebar-mode", toolParam);
    if (wsMgr) wsMgr.fSidebarMode = toolParam;
    if (!sideBarExpanded) {
      setSideBarExpanded(true);
      localStorage.setItem("sidebar-expanded", "true");
    }

    ResizeCanvasOnChange();
  }, [toolParam, chartDocument, canModifyBackgrounds]);

  const closeSidebar = useCallback(
    (ignoreBlocker = false) => {
      if (sideBarExpanded) {
        if (!ignoreBlocker) {
          const onProceed = () => closeSidebar(true);
          if (shouldBlock("rightSidebarChangeMode", onProceed)) {
            return;
          }
        }
        setModeDirty(false);
        setSideBarExpanded(false);
        setCustomHeaderProps({});
        localStorage.removeItem("sidebar-expanded");
        localStorage.removeItem("sidebar-mode");
        setSideBarMode(null);
        const wsMgr = GetWSMgr();
        if (wsMgr) wsMgr.fSidebarMode = null;
        setRouteParam("tool", null);
      }
      ResizeCanvasOnChange();
    },
    [sideBarExpanded, shouldBlock, ResizeCanvasOnChange, setRouteParam]
  );

  const handleSideBarModeChange = useCallback(
    (targetMode, ignoreBlocker = false) => {
      if (!targetMode) {
        closeSidebar(ignoreBlocker);
        return;
      }
      if (!ignoreBlocker) {
        const onProceed = () => handleSideBarModeChange(targetMode, true);
        if (shouldBlock("rightSidebarChangeMode", onProceed, targetMode)) {
          return;
        }
      }

      // If switching away from the back-target's activeInMode, the
      // chevron is no longer relevant — clear the back-target so a
      // future tool switch doesn't re-render the chevron incorrectly.
      if (sidebarBackTarget && targetMode !== sidebarBackTarget.activeInMode) {
        setSidebarBackTarget(null);
      }

      // Every tool — just write ?tool=. ?mode= is never touched by sidebar
      // clicks. Background sub-mode activates via the toolParam effect.
      setRouteParam("tool", targetMode);
    },
    [closeSidebar, shouldBlock, setRouteParam, sidebarBackTarget]
  );

  const navigateWithBackTarget = useCallback(
    (targetMode, backTarget, ignoreBlocker = false) => {
      if (!targetMode || !backTarget) return;
      setSidebarBackTarget({
        ...backTarget,
        activeInMode: targetMode
      });
      handleSideBarModeChange(targetMode, ignoreBlocker);
    },
    [handleSideBarModeChange]
  );

  const setHighlightedRecordIds = recordIds => {
    setStagingState(p => ({ ...p, highlightedRecordIds: recordIds }));
  };

  const highlightRecord = dataObj => {
    const recordId = dataObj.GetObjectRecordID();
    setStagingState({ root: dataObj.GetParent(), highlightedRecordIds: [recordId] });
  };

  useEffect(() => {
    SubscribeExternalModuleReactAPI(
      { handleSideBarModeChange, sideBarMode, setHighlightedRecordIds },
      "sidebar"
    );
  }, []);

  const contextValue = useMemo(
    () => ({
      sideBarMode,
      sideBarExpanded,
      setSideBarExpanded,
      openSidebar,
      closeSidebar,
      handleSideBarModeChange,
      navigateWithBackTarget,
      ResizeCanvasOnChange,
      stagingState,
      setStagingState,
      highlightRecord,
      settingsOpenCtrlRef,
      settingsFloatingCtrlRef,
      isModeDirty,
      setModeDirty,
      customHeaderProps,
      setCustomHeaderProps,
      sidebarBackTarget,
      setSidebarBackTarget
    }),
    [
      sideBarMode,
      sideBarExpanded,
      stagingState,
      isModeDirty,
      customHeaderProps,
      sidebarBackTarget,
      openSidebar,
      closeSidebar,
      handleSideBarModeChange,
      navigateWithBackTarget,
      ResizeCanvasOnChange
    ]
  );

  return (
    <RightSidebarContext.Provider value={contextValue}>{children}</RightSidebarContext.Provider>
  );
};

export const useRightSidebarContext = () => useContext(RightSidebarContext);

export default RightSidebarProvider;
